import { Coupon, CouponDetailType } from 'types/coupon'
import { type ListFpMemberCouponsQuery } from 'graphQL/dinein/generate/operations'
import { type ListPointRedemptionCampaignsQuery } from 'graphQL/hatostore/generate/operations'
import { getPropertyByLang } from 'helpers/i18next'
import { getResizableURL } from 'helpers/image'
import dayjs from 'dayjs'

type ListFpMemberCoupons = ListFpMemberCouponsQuery['listFPMemberCoupons']
type FpCampaigns = ListPointRedemptionCampaignsQuery['listPointRedemptionCampaigns']
const defaultCampaignImage = '/assets/generic/promotions/coupon.svg'

export const convertListFPMemberCouponsToCoupons = ({
  listFPMemberCoupons,
  currentMembershipId,
  allowedSharingCoupon,
}: {
  listFPMemberCoupons: ListFpMemberCoupons
  currentMembershipId?: number
  allowedSharingCoupon: boolean
}) => {
  const coupons: Coupon[] = []
  listFPMemberCoupons.forEach(({ serialNumber, imageURL, name, endTime, membership }) => {
    const currentUserNotAllowedSharing = membership && membership.id === currentMembershipId && !allowedSharingCoupon
    coupons.push({
      serialNumber,
      thumbnailImageURL: imageURL,
      name,
      endTime,
      lineUser: currentUserNotAllowedSharing ? undefined : membership.user,
    })
  })
  return coupons
}

export const convertFpMemberCampaignsToCouponDetails = (fpCampaigns: FpCampaigns) => {
  const couponDetails: CouponDetailType[] = []
  fpCampaigns.forEach(
    ({
      id,
      images,
      nameEn,
      nameTh,
      startTime,
      endTime,
      points,
      conditionDetailsEn,
      conditionDetailsTh,
      invalidConditions,
    }) => {
      const thumbnailImageURL =
        images.length > 0
          ? getResizableURL(images, { width: '100', height: '100' }, defaultCampaignImage)
          : defaultCampaignImage
      couponDetails.push({
        serialNumber: String(id),
        name: getPropertyByLang({ en: nameEn, th: nameTh }),
        description: getPropertyByLang({ en: conditionDetailsEn, th: conditionDetailsTh }),
        thumbnailImageURL,
        startTime,
        endTime,
        pointsRequire: points,
        invalidConditions,
      })
    }
  )
  return couponDetails
}

export const getThumbnailFpCampaignById = (id: number, fpCampaigns: FpCampaigns) => {
  const findFpCampaign = fpCampaigns.find((c) => c.id === id)
  if (!findFpCampaign || findFpCampaign.images.length === 0) return defaultCampaignImage

  const thumbnailImage = getResizableURL(findFpCampaign.images, { width: '600', height: '600' }, defaultCampaignImage)
  return thumbnailImage
}

interface TableUserGuest {
  userID: number
  allowSharingCoupon: boolean
}
export const isCurrentUserAllowedSharingCoupon = (currentUserId: number, tableUserGuest: TableUserGuest[]) => {
  return tableUserGuest.some(({ userID, allowSharingCoupon }) => userID === currentUserId && allowSharingCoupon)
}

export const countTotalUserSharedCoupons = (tableUserGuest: TableUserGuest[]) => {
  return tableUserGuest.reduce((total, { allowSharingCoupon }) => (allowSharingCoupon ? total + 1 : total), 0)
}

export const sortCouponsByEndTime = <T extends { endTime: string }>(coupons: T[]): T[] => {
  return coupons.sort((a, b) => {
    const dateA = dayjs(a.endTime)
    const dateB = dayjs(b.endTime)
    return dateA.valueOf() - dateB.valueOf()
  })
}

export const isExpireSoon = (expirationDays, endTime: string) => {
  return dayjs().add(expirationDays, 'days').isAfter(endTime)
}
